import { CorrespondenceIssuedCategoryDto } from '@cradua/data';
import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as CorrespondenceIssuedCategoryActions from './issued-category.actions';

export const CORRESPONDENCE_ISSUED_CATEGORY_FEATURE_KEY =
  'correspondenceIssuedCategory';

export interface CorrespondenceIssuedCategoryState
  extends EntityState<CorrespondenceIssuedCategoryDto> {
  selectedCorrespondenceIssuedCategoryId?: string | number; // which CorrespondenceIssuedCategory record has been selected
  loaded: boolean; // has the CorrespondenceIssuedCategory list been loaded
  error?: string | null; // last none error (if any)
}

export interface CorrespondenceIssuedCategoryPartialState {
  readonly [CORRESPONDENCE_ISSUED_CATEGORY_FEATURE_KEY]: CorrespondenceIssuedCategoryState;
}

export const correspondenceIssuedCategoryAdapter: EntityAdapter<CorrespondenceIssuedCategoryDto> = createEntityAdapter<
  CorrespondenceIssuedCategoryDto
>({
  selectId: (correspondenceIssuedCategory) => correspondenceIssuedCategory.id,
  sortComparer: false,
});

export const initialState: CorrespondenceIssuedCategoryState = correspondenceIssuedCategoryAdapter.getInitialState(
  {
    // set initial required properties
    loaded: false,
    error: null,
    selectedCorrespondenceIssuedCategoryId: null,
  }
);

const correspondenceIssuedCategoryReducer = createReducer(
  initialState,
  on(
    CorrespondenceIssuedCategoryActions.loadCorrespondenceIssuedCategory,
    (state) => ({
      ...state,
      loaded: true,
      error: null,
    })
  ),
  on(
    CorrespondenceIssuedCategoryActions.loadCorrespondenceIssuedCategorySuccess,
    (state, { correspondenceIssuedCategory }) =>
      correspondenceIssuedCategoryAdapter.addAll(correspondenceIssuedCategory, {
        ...state,
        loaded: false,
      })
  ),
  on(
    CorrespondenceIssuedCategoryActions.loadCorrespondenceIssuedCategoryFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(
    CorrespondenceIssuedCategoryActions.addCorrespondenceIssuedCategory,
    (state) => ({
      ...state,
      loaded: true,
      error: null,
    })
  ),
  on(
    CorrespondenceIssuedCategoryActions.addCorrespondenceIssuedCategorySuccess,
    (state, { newCorrespondenceIssuedCategory }) =>
      correspondenceIssuedCategoryAdapter.addMany(
        newCorrespondenceIssuedCategory,
        { ...state, loaded: false }
      )
  ),
  on(
    CorrespondenceIssuedCategoryActions.addCorrespondenceIssuedCategoryFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(
    CorrespondenceIssuedCategoryActions.updateCorrespondenceIssuedCategory,
    (state) => ({
      ...state,
      loaded: true,
      error: null,
    })
  ),
  on(
    CorrespondenceIssuedCategoryActions.updateCorrespondenceIssuedCategorySuccess,
    (state, { updateCorrespondenceIssuedCategory }) =>
      correspondenceIssuedCategoryAdapter.upsertMany(
        updateCorrespondenceIssuedCategory,
        { ...state, loaded: false }
      )
  ),
  on(
    CorrespondenceIssuedCategoryActions.updateCorrespondenceIssuedCategoryFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(
    CorrespondenceIssuedCategoryActions.removeCorrespondenceIssuedCategory,
    (state) => ({
      ...state,
      loaded: true,
      error: null,
    })
  ),
  on(
    CorrespondenceIssuedCategoryActions.removeCorrespondenceIssuedCategorySuccess,
    (state, { ids }) =>
      correspondenceIssuedCategoryAdapter.removeMany(ids, {
        ...state,
        loaded: false,
      })
  ),
  on(
    CorrespondenceIssuedCategoryActions.removeCorrespondenceIssuedCategoryFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  )
);

export function reducer(
  state: CorrespondenceIssuedCategoryState | undefined,
  action: Action
) {
  return correspondenceIssuedCategoryReducer(state, action);
}
